import React, { createContext, useContext, useState, useEffect } from 'react';
import { affiliateAuthAPI } from '../utils/api';

const AffiliateAuthContext = createContext();

export const useAffiliateAuth = () => {
  const context = useContext(AffiliateAuthContext);
  if (!context) {
    throw new Error('useAffiliateAuth must be used within an AffiliateAuthProvider');
  }
  return context;
};

export const AffiliateAuthProvider = ({ children }) => {
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check for existing token and affiliate data on app load
    const storedToken = localStorage.getItem('affiliateToken');
    const storedAffiliate = localStorage.getItem('affiliate');

    if (storedToken && storedAffiliate) {
      try {
        const affiliateData = JSON.parse(storedAffiliate);
        if (affiliateData.email_verified) {
          setToken(storedToken);
          setAffiliate(affiliateData);
        } else {
          localStorage.removeItem('affiliateToken');
          localStorage.removeItem('affiliate');
        }
      } catch (error) {
        console.error('Error parsing stored affiliate data:', error);
        localStorage.removeItem('affiliateToken');
        localStorage.removeItem('affiliate');
      }
    }
    setLoading(false);

    // Listen for logout events
    const handleLogout = () => {
      setAffiliate(null);
      setToken(null);
      localStorage.removeItem('affiliateToken');
      localStorage.removeItem('affiliate');
    };

    window.addEventListener('affiliate:logout', handleLogout);
    return () => window.removeEventListener('affiliate:logout', handleLogout);
  }, []);

  const register = async (username, email, phone, country, password) => {
    try {
      const response = await affiliateAuthAPI.register(username, email, phone, country, password);
      return {
        success: true,
        affiliate: response.affiliate,
        requiresVerification: response.requiresVerification,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  };

  const login = async (email, password) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_BASE_URL}/affiliates/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          success: false,
          error: data.message || data.error || 'Login failed',
          requiresVerification: data.requiresVerification === true,
          affiliateId: data.affiliateId || null
        };
      }
      if (data.token && data.affiliate) {
        setToken(data.token);
        setAffiliate(data.affiliate);
        localStorage.setItem('affiliateToken', data.token);
        localStorage.setItem('affiliate', JSON.stringify(data.affiliate));
        return { success: true };
      }
      return { success: false, error: data.message || 'Login failed' };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  };

  const logout = () => {
    setAffiliate(null);
    setToken(null);
    localStorage.removeItem('affiliateToken');
    localStorage.removeItem('affiliate');
    window.dispatchEvent(new CustomEvent('affiliate:logout'));
  };

  const verifyEmail = async (token) => {
    try {
      const response = await affiliateAuthAPI.verifyEmail(token);

      if (response.affiliate) {
        return {
          success: true,
          message: response.message || 'Email verified successfully. Your account is pending admin approval.',
          requiresAdminApproval: response.requiresAdminApproval !== false
        };
      }

      return { success: false, error: response.message || 'Email verification failed' };
    } catch (error) {
      console.error('AffiliateAuthContext: Verification error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  };

  const resendVerification = async (affiliateId) => {
    try {
      const response = await affiliateAuthAPI.resendVerification(affiliateId);
      return {
        success: true,
        message: response.message || 'Verification email sent successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to resend verification email'
      };
    }
  };

  const value = {
    affiliate,
    token,
    loading,
    register,
    login,
    logout,
    verifyEmail,
    resendVerification,
    isAuthenticated: !!affiliate && !!token
  };

  return (
    <AffiliateAuthContext.Provider value={value}>
      {children}
    </AffiliateAuthContext.Provider>
  );
};

